<script setup>
import { initPosDisplayEcho } from '@/plugins/echo'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

// Modul AS (Addendum 11 §2) — halaman display pelanggan (Customer Display
// Screen) sungguhan. Device kedua ini TIDAK PERNAH login staf -- token
// pairing (dari Modul AR, pos-display/connect) di query string adalah
// satu-satunya kredensial, dikirim sebagai header X-Pos-Display-Token ke
// /broadcasting/auth lewat initPosDisplayEcho().
//
// CATATAN: broadcast real-time belum benar-benar aktif di lingkungan dev ini
// (VITE_PUSHER_APP_KEY belum di-set, driver backend masih 'log') --
// initPosDisplayEcho() akan no-op (lihat plugins/echo.js) sampai driver push
// sungguhan (mis. Reverb) diaktifkan, keputusan terpisah di luar modul ini.

const route = useRoute('pos-display-shift-id')

const token = route.query.token
const tenantId = route.query.tenant_id
const shiftId = route.params.shiftId

// bisa diatur lewat ?idle_seconds=N di URL pairing; default 8 detik sesuai
// requirement Modul AS §2.1.4.
const idleResetSeconds = Number(route.query.idle_seconds) || 8

const connectionStatus = ref(token ? 'connecting' : 'no-token')

// phase: 'idle' (belum ada transaksi / sudah direset) | 'cart' (keranjang
// berjalan) | 'paid' (baru checkout, tampilkan kembalian)
const phase = ref('idle')
const cartData = ref(null)
const receiptData = ref(null)
const countdown = ref(0)

let resetTimer = null
let countdownInterval = null

const formatRupiah = value => `Rp ${Number(value || 0).toLocaleString('id-ID')}`

const clearResetTimers = () => {
  clearTimeout(resetTimer)
  clearInterval(countdownInterval)
}

const backToIdle = () => {
  clearResetTimers()
  phase.value = 'idle'
  cartData.value = null
  receiptData.value = null
}

const scheduleReset = seconds => {
  clearResetTimers()
  countdown.value = seconds
  countdownInterval = setInterval(() => {
    if (countdown.value <= 1) {
      clearInterval(countdownInterval)
    } else {
      countdown.value -= 1
    }
  }, 1000)
  resetTimer = setTimeout(backToIdle, seconds * 1000)
}

const handleCartUpdated = payload => {
  if (payload?.status === 'paid') {
    clearResetTimers()
    receiptData.value = payload
    cartData.value = null
    phase.value = 'paid'
    scheduleReset(idleResetSeconds)

    return
  }

  // status 'cart' (atau tidak ada status sama sekali, untuk backward-compat).
  // Keranjang kosong (mis. baru dikosongkan kasir) -> kembali ke idle,
  // bukan menampilkan kartu kosong.
  clearResetTimers()
  if (!payload?.items?.length) {
    phase.value = 'idle'
    cartData.value = null

    return
  }

  cartData.value = payload
  phase.value = 'cart'
}

onMounted(() => {
  if (!token)
    return

  const echo = initPosDisplayEcho(token)

  if (!echo) {
    connectionStatus.value = 'realtime-disabled'

    return
  }

  echo.private(`tenant.${tenantId}.pos-display.${shiftId}`)
    .subscribed(() => { connectionStatus.value = 'connected' })
    .error(() => { connectionStatus.value = 'rejected' })
    .listen('.PosCartUpdated', handleCartUpdated)
})

onBeforeUnmount(clearResetTimers)
</script>

<template>
  <div class="cds-screen">
    <template v-if="connectionStatus === 'no-token' || connectionStatus === 'rejected'">
      <div class="cds-status-card">
        <VIcon
          icon="ri-error-warning-line"
          size="56"
          color="error"
          class="mb-4"
        />
        <h4 class="text-h4 mb-2">
          Tidak Bisa Terhubung
        </h4>
        <p class="text-body-1 text-medium-emphasis">
          {{ connectionStatus === 'no-token'
            ? 'Token tidak ditemukan. Silakan hubungkan ulang lewat halaman pairing.'
            : 'Otorisasi ditolak — token sudah kedaluwarsa atau shift sudah ditutup.' }}
        </p>
      </div>
    </template>

    <template v-else-if="connectionStatus === 'connecting'">
      <div class="cds-status-card">
        <VProgressCircular
          indeterminate
          color="primary"
          size="48"
          class="mb-4"
        />
        <h4 class="text-h4">
          Menghubungkan...
        </h4>
      </div>
    </template>

    <!--
      Kalau tidak eksplisit ditangani, halaman ini akan diam-diam jatuh ke
      tampilan idle "Selamat Datang" yang IDENTIK dengan idle normal --
      padahal sama sekali belum pernah subscribe channel apa pun, jadi
      TIDAK AKAN PERNAH update walau kasir mengubah keranjang. Harus jadi
      layar tersendiri yang jelas, bukan menyamar sebagai idle biasa. 
    -->
    <template v-else-if="connectionStatus === 'realtime-disabled'">
      <div class="cds-status-card">
        <VIcon
          icon="ri-wifi-off-line"
          size="56"
          color="warning"
          class="mb-4"
        />
        <h4 class="text-h4 mb-2">
          Realtime Belum Aktif
        </h4>
        <p class="text-body-1 text-medium-emphasis">
          Server ini belum dikonfigurasi untuk update langsung. Hubungi admin sistem.
        </p>
      </div>
    </template>

    <!-- ── PHASE: PAID — ringkasan akhir, kembalian paling menonjol ────── -->
    <template v-else-if="phase === 'paid' && receiptData">
      <div class="cds-receipt">
        <VIcon
          icon="ri-checkbox-circle-fill"
          size="64"
          color="success"
          class="mb-2"
        />
        <h3 class="text-h3 font-weight-bold mb-1">
          Pembayaran Berhasil
        </h3>
        <p class="text-body-1 text-medium-emphasis mb-8">
          {{ receiptData.order_number }}
        </p>

        <div class="cds-receipt-row">
          <span>Total Tagihan</span>
          <span>{{ formatRupiah(receiptData.total) }}</span>
        </div>
        <div class="cds-receipt-row">
          <span>Dibayar</span>
          <span>{{ formatRupiah(receiptData.total_paid) }}</span>
        </div>

        <div class="cds-change-box">
          <span class="cds-change-label">Kembalian</span>
          <span class="cds-change-amount">{{ formatRupiah(receiptData.change_amount) }}</span>
        </div>

        <p class="text-caption text-medium-emphasis mt-6">
          Kembali ke layar utama dalam {{ countdown }} detik...
        </p>
      </div>
    </template>

    <!-- ── PHASE: CART — keranjang berjalan, real-time ──────────────────── -->
    <template v-else-if="phase === 'cart' && cartData">
      <div class="cds-cart">
        <div class="cds-cart-items">
          <div
            v-for="(item, idx) in cartData.items"
            :key="idx"
            class="cds-cart-item"
          >
            <div class="cds-cart-item-info">
              <div class="cds-cart-item-name">
                {{ item.product_name }}<span v-if="item.variant_name"> — {{ item.variant_name }}</span>
              </div>
              <div class="cds-cart-item-meta">
                {{ item.quantity }} x {{ formatRupiah(item.unit_price) }}
              </div>
            </div>
            <div class="cds-cart-item-subtotal">
              {{ formatRupiah(item.subtotal) }}
            </div>
          </div>
        </div>

        <div class="cds-cart-summary">
          <div class="cds-summary-row">
            <span>Subtotal</span>
            <span>{{ formatRupiah(cartData.subtotal) }}</span>
          </div>

          <div
            v-for="(line, idx) in cartData.discount_breakdown"
            :key="idx"
            class="cds-summary-row cds-summary-row--discount"
          >
            <span>{{ line.label }}</span>
            <span>-{{ formatRupiah(line.amount) }}</span>
          </div>

          <div
            v-if="cartData.tax_amount > 0"
            class="cds-summary-row"
          >
            <span>Pajak</span>
            <span>{{ formatRupiah(cartData.tax_amount) }}</span>
          </div>

          <div class="cds-summary-row cds-summary-row--total">
            <span>Total</span>
            <span>{{ formatRupiah(cartData.total) }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- ── PHASE: IDLE — belum ada transaksi berjalan ───────────────────── -->
    <template v-else>
      <div class="cds-idle">
        <VIcon
          icon="ri-cup-line"
          size="72"
          class="mb-4"
        />
        <h2 class="text-h2 font-weight-bold mb-2">
          Selamat Datang
        </h2>
        <p class="text-h6 font-weight-regular cds-idle-subtitle">
          Menunggu pesanan Anda...
        </p>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* Layar kiosk pelanggan — SENGAJA warna & ukuran font hardcoded, tidak
   mengikuti theme toggle app (sama alasannya dengan .kds-screen di
   pages/kds/[stationId].vue): device ini selalu dilihat pelanggan dari
   jarak, bukan staf yang punya preferensi dark/light mode sendiri. */
.cds-screen {
  min-block-size: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #14161f;
  color: #f5f5f7;
  padding: clamp(16px, 4vw, 48px);
  font-size: clamp(16px, 1.4vw, 22px);

  /* Layar ini kiosk background GELAP yang di-hardcode di atas (bukan ikut
     tema app). --v-theme-on-surface DIPAKAI beberapa utility Vuetify (mis.
     text-medium-emphasis lewat --v-medium-emphasis-opacity), jadi tetap
     dipaksa terang di sini -- tapi heading (h1-h6, dipakai lewat class
     text-h2/h3/h4) TERNYATA warnanya datang dari CSS global template
     Materialize (bukan dari token Vuetify sama sekali, dikonfirmasi lewat
     computed style: rgba(38,43,67,.9) tetap muncul walau token di atas
     sudah di-override) -- makanya heading & paragraf dipaksa lagi eksplisit
     di bawah lewat selector element biasa, elemen demi elemen. */
  --v-theme-on-surface: 245, 245, 247;
}

.cds-screen h1,
.cds-screen h2,
.cds-screen h3,
.cds-screen h4,
.cds-screen h5,
.cds-screen h6,
.cds-screen p {
  color: inherit;
}

.cds-status-card {
  text-align: center;
  max-inline-size: 480px;
}

.cds-idle {
  text-align: center;
}
.cds-idle-subtitle {
  color: #a3a6b8;
}

.cds-cart {
  inline-size: min(720px, 92vw);
}
.cds-cart-items {
  max-block-size: 60vh;
  overflow-y: auto;
  margin-block-end: 1rem;
}
.cds-cart-item {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-block: 0.6em;
  border-block-end: 1px solid rgba(255, 255, 255, 0.08);
}
.cds-cart-item-name {
  font-size: 1.2em;
  font-weight: 600;
}
.cds-cart-item-meta {
  font-size: 0.85em;
  color: #a3a6b8;
}
.cds-cart-item-subtotal {
  font-size: 1.2em;
  font-weight: 600;
  white-space: nowrap;
}

.cds-cart-summary {
  border-block-start: 2px solid rgba(255, 255, 255, 0.15);
  padding-block-start: 0.75rem;
}
.cds-summary-row {
  display: flex;
  justify-content: space-between;
  padding-block: 0.3em;
  font-size: 1.05em;
}
.cds-summary-row--discount {
  color: #6ee7a0;
}
.cds-summary-row--total {
  font-size: 1.8em;
  font-weight: 700;
  padding-block-start: 0.5em;
  margin-block-start: 0.25em;
  border-block-start: 1px solid rgba(255, 255, 255, 0.15);
}

.cds-receipt {
  text-align: center;
  inline-size: min(560px, 92vw);
}
.cds-receipt-row {
  display: flex;
  justify-content: space-between;
  font-size: 1.15em;
  padding-block: 0.35em;
}
.cds-change-box {
  margin-block-start: 1.5rem;
  padding: 1.25rem;
  border-radius: 16px;
  background: #1f7a4c;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.cds-change-label {
  font-size: 1.1em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.cds-change-amount {
  font-size: clamp(2.5rem, 8vw, 4.5rem);
  font-weight: 800;
  line-height: 1.1;
}
</style>
